import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// 可搜索下拉: 原生 select 的替代.
// 两种模式:
//   1. 静态模式 (options): 选项全量在内存, 本地过滤. 适合几十~几百条 (如设备列表).
//   2. 异步模式 (loadOptions): 打开只拉第一页, 搜索走服务端 (防抖), 滚动到底部翻页.
//      适合上千条的大列表 (如材料), 避免一次性全量拉取.
// 展开后自动聚焦搜索框; Enter 选中第一个匹配项; Esc / 点击外部关闭.
// 浮层走 portal 挂到 body + fixed 定位: 避免被 overflow 容器 (弹窗字段列表 / 表格) 裁掉;
// 下方空间不足时自动向上翻, 祖先滚动或窗口尺寸变化时重算位置.

export interface SearchSelectOption {
    value: string;
    label: string;
}

export interface SearchSelectLoadResult {
    list: SearchSelectOption[];
    hasMore: boolean;
}

interface Props {
    /** 静态模式: 全量选项 */
    options?: SearchSelectOption[];
    /** 异步模式: 按 (关键词, 页码) 拉取一页; 传入后忽略 options */
    loadOptions?: (query: string, page: number) => Promise<SearchSelectLoadResult>;
    value: string;
    onChange: (v: string, option?: SearchSelectOption) => void;
    /** 异步模式下当前选中项的展示文案 (选中项可能不在已加载页里) */
    valueLabel?: string;
    placeholder?: string;
    /** 宽度等布局类, 默认 w-72 */
    className?: string;
    disabled?: boolean;
    /** 可清空: 有值时显示 ✕, 点击置空 (筛选场景用) */
    clearable?: boolean;
    /** 可新建 (仅静态模式): 搜索词不在选项里时顶部出「+ 新建「词」」, 选中即以该词为值 (取代原生 datalist) */
    creatable?: boolean;
}

// 静态模式过滤结果最多渲染的行数 (防上千 DOM 节点卡顿)
const MAX_RENDER = 300;
// 服务端搜索防抖
const DEBOUNCE_MS = 300;
// 距底部多少像素内触发翻页
const NEAR_BOTTOM_PX = 40;
// 浮层最大高度 (搜索框 + 列表), 用于判断上下翻转
const POPUP_MAX_H = 320;
// 浮层与触发按钮的间距
const POPUP_GAP = 4;

function SearchSelect({
    options,
    loadOptions,
    value,
    onChange,
    valueLabel,
    placeholder = '键入搜索…',
    className = 'w-72',
    disabled = false,
    clearable = false,
    creatable = false,
}: Props) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // 异步模式: 已加载选项 + 翻页状态
    const [asyncList, setAsyncList] = useState<SearchSelectOption[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const pageRef = useRef(1);
    // 竞态防护: 只采纳最后一次发出的请求
    const reqSeq = useRef(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    // 浮层 fixed 定位 (相对视口), 展开时按触发按钮位置算
    const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxH: number }>({
        left: 0,
        width: 0,
        top: 0,
        maxH: POPUP_MAX_H,
    });

    const isAsync = !!loadOptions;

    const doLoad = useCallback(
        async (q: string, page: number, append: boolean) => {
            if (!loadOptions) return;
            reqSeq.current += 1;
            const seq = reqSeq.current;
            setLoading(true);
            try {
                const r = await loadOptions(q, page);
                if (seq !== reqSeq.current) return; // 已被更新的请求取代
                pageRef.current = page;
                setHasMore(r.hasMore);
                setAsyncList((prev) => (append ? [...prev, ...r.list] : r.list));
            } catch {
                if (seq === reqSeq.current) setHasMore(false);
            } finally {
                if (seq === reqSeq.current) setLoading(false);
            }
        },
        [loadOptions],
    );

    // 异步模式: 打开时拉第一页; 关键词变化防抖后重查第一页
    useEffect(() => {
        if (!open || !isAsync) return undefined;
        const t = setTimeout(
            () => doLoad(query.trim(), 1, false),
            query === '' ? 0 : DEBOUNCE_MS,
        );
        return () => clearTimeout(t);
    }, [open, isAsync, query, doLoad]);

    // 按触发按钮的视口位置定位浮层; 下方不够高就向上翻, 并按可用空间收窄最大高度
    const reposition = useCallback(() => {
        const el = rootRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const below = window.innerHeight - r.bottom - POPUP_GAP * 2;
        const above = r.top - POPUP_GAP * 2;
        const flipUp = below < Math.min(POPUP_MAX_H, above);
        setPos({
            left: r.left,
            width: r.width,
            ...(flipUp
                ? { bottom: window.innerHeight - r.top + POPUP_GAP }
                : { top: r.bottom + POPUP_GAP }),
            maxH: Math.max(160, Math.min(POPUP_MAX_H, flipUp ? above : below)),
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        reposition();
        // 捕获阶段监听: 任意祖先滚动容器滚动都要跟随
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [open, reposition]);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            // 浮层在 portal 里, 不属于 rootRef 子树, 需单独排除
            if (
                rootRef.current &&
                !rootRef.current.contains(t) &&
                !(popupRef.current && popupRef.current.contains(t))
            ) {
                setOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const filteredStatic = useMemo(() => {
        if (isAsync) return [];
        const all = options || [];
        // 空格分词, 每个词都要命中 (如 "GS009 填充" 同时按机型和加工类型筛)
        const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (words.length === 0) return all;
        return all.filter((o) => {
            const text = `${o.label} ${o.value}`.toLowerCase();
            return words.every((w) => text.includes(w));
        });
    }, [isAsync, options, query]);

    const shown = isAsync ? asyncList : filteredStatic.slice(0, MAX_RENDER);
    // 可新建: 搜索词与任何选项都不完全相同时才给新建项
    const typed = query.trim();
    const createOpt: SearchSelectOption | null =
        creatable && !isAsync && typed && !(options || []).some((o) => o.value === typed || o.label === typed)
            ? { value: typed, label: typed }
            : null;
    const staticOverflow = !isAsync && filteredStatic.length > MAX_RENDER;

    // 选中项展示: 优先外部给的 valueLabel, 其次在已知选项里找, 最后退回 value 本身
    const currentLabel =
        (value &&
            (valueLabel ||
                (isAsync ? asyncList : options || []).find((o) => o.value === value)?.label ||
                value)) ||
        '';

    const pick = (o: SearchSelectOption) => {
        onChange(o.value, o);
        setOpen(false);
    };

    // 滚动到底部触发翻页 (仅异步模式)
    const onListScroll = () => {
        if (!isAsync || loading || !hasMore) return;
        const el = listRef.current;
        if (!el) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - NEAR_BOTTOM_PX) {
            doLoad(query.trim(), pageRef.current + 1, true);
        }
    };

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                type="button"
                className="sel w-full text-left flex items-center gap-1"
                disabled={disabled}
                title={currentLabel}
                onClick={() => {
                    if (!open) {
                        setQuery('');
                        // 展开后聚焦搜索框 (等浮层渲染完成)
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }
                    setOpen((o) => !o);
                }}
            >
                <span className="flex-1 truncate">
                    {currentLabel || <span className="text-slate-400">{placeholder}</span>}
                </span>
                {clearable && value && (
                    <span
                        role="button"
                        tabIndex={-1}
                        className="text-slate-400 hover:text-slate-600 text-xs px-0.5"
                        title="清空"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange('');
                            setOpen(false);
                        }}
                    >
                        ✕
                    </span>
                )}
                {/* 下拉箭头由 .sel 的背景 SVG 统一画 (index.html), 这里不再手写 ▾, 否则双箭头 */}
            </button>
            {open &&
                createPortal(
                    <div
                        ref={popupRef}
                        className="fixed z-[60] bg-white border border-slate-200 rounded-lg shadow-lg flex flex-col"
                        style={{
                            left: pos.left,
                            width: pos.width,
                            top: pos.top,
                            bottom: pos.bottom,
                            maxHeight: pos.maxH,
                        }}
                    >
                        <div className="p-2 border-b border-slate-100 shrink-0">
                            <input
                                ref={inputRef}
                                className="inp w-full"
                                placeholder={placeholder}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (shown.length > 0) pick(shown[0]);
                                        else if (createOpt) pick(createOpt);
                                    }
                                }}
                            />
                        </div>
                        <div
                            ref={listRef}
                            className="flex-1 min-h-0 overflow-auto py-1"
                            onScroll={onListScroll}
                        >
                            {createOpt && (
                                <button
                                    type="button"
                                    className="block w-full text-left px-3 py-1.5 text-sm text-brand-600 hover:bg-brand-50 truncate"
                                    title={`新建「${createOpt.label}」`}
                                    onClick={() => pick(createOpt)}
                                >
                                    + 新建「{createOpt.label}」
                                </button>
                            )}
                            {shown.length === 0 && !loading && !createOpt && (
                                <div className="px-3 py-2 text-xs text-slate-400">无匹配项</div>
                            )}
                            {shown.map((o) => (
                                <button
                                    type="button"
                                    key={o.value}
                                    className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 truncate ${
                                        o.value === value ? 'text-brand-600 bg-brand-50' : ''
                                    }`}
                                    title={o.label}
                                    onClick={() => pick(o)}
                                >
                                    {o.label}
                                </button>
                            ))}
                            {loading && <div className="px-3 py-2 text-xs text-slate-400">加载中…</div>}
                            {isAsync && !loading && hasMore && (
                                <div className="px-3 py-2 text-xs text-slate-300">下滑加载更多…</div>
                            )}
                            {staticOverflow && (
                                <div className="px-3 py-2 text-xs text-slate-400">
                                    已显示前 {MAX_RENDER} 项 (共 {filteredStatic.length}), 继续输入缩小范围
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}

export default SearchSelect;
